/**
 * Vehicle Card Component
 * Compact card showing vehicle/device info in the side panel
 */

import React from 'react';
import { View, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { Ionicons, FontAwesome5 } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { useTheme, Text, Surface } from 'react-native-paper';
import type { LivePosition } from '../../types/gps';

interface VehicleCardProps {
  position: LivePosition;
  isSelected: boolean;
  onPress: () => void;
  onLocate: () => void;
  onEditVehicleLink?: () => void;
}

export default function VehicleCard({
  position,
  isSelected,
  onPress,
  onLocate,
  onEditVehicleLink,
}: VehicleCardProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const statusColor = position.is_online ? theme.colors.primary : theme.colors.outline;
  const speedKmh = Math.round(position.speed || 0);
  
  const formatTimestamp = (timestamp: string | null) => {
    if (!timestamp) return t('gps.noData');
    
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        return timestamp; // Return original if invalid date
      }
      
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins < 1) {
        const nowText = t('common.now');
        return nowText !== 'common.now' ? nowText : 'Agora';
      }
      
      if (diffMins < 60) {
        // Try to get translation, but use fallback if it fails
        let translation = t('common.minutesAgo', { count: diffMins });
        // If translation returns the key or contains 'common.', use fallback
        if (!translation || translation === 'common.minutesAgo' || translation.includes('common.')) {
          translation = `${diffMins}m atrás`;
        }
        // Replace {{count}} manually if interpolation didn't work
        return translation.replace('{{count}}', diffMins.toString());
      }
      
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) {
        let translation = t('common.hoursAgo', { count: diffHours });
        if (!translation || translation === 'common.hoursAgo' || translation.includes('common.')) {
          translation = `${diffHours}h atrás`;
        }
        return translation.replace('{{count}}', diffHours.toString());
      }
      
      const diffDays = Math.floor(diffHours / 24);
      let translation = t('common.daysAgo', { count: diffDays });
      if (!translation || translation === 'common.daysAgo' || translation.includes('common.')) {
        translation = `${diffDays}d atrás`;
      }
      return translation.replace('{{count}}', diffDays.toString());
    } catch (error) {
      // Fallback to formatted date if parsing fails
      try {
        const date = new Date(timestamp);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('pt-PT', { 
            day: '2-digit', 
            month: '2-digit', 
            hour: '2-digit', 
            minute: '2-digit' 
          });
        }
      } catch {
        // Ignore
      }
      return timestamp;
    }
  };

  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.7}
    >
      <Surface
        style={[
          styles.card, 
          { backgroundColor: theme.colors.surface, borderColor: theme.colors.outlineVariant },
          isSelected && { borderColor: theme.colors.primary, borderWidth: 2, backgroundColor: theme.colors.primaryContainer }
        ]}
        elevation={isSelected ? 2 : 1}
      >
        {/* Status Indicator */}
        <View style={[styles.statusBar, { backgroundColor: statusColor }]} />
        
        <View style={styles.cardContent}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.titleRow}>
              <FontAwesome5 
                name="car" 
                size={16} 
                color={isSelected ? theme.colors.primary : theme.colors.onSurface}
                solid
              />
              <Text 
                variant="titleSmall"
                style={[styles.deviceName, { color: theme.colors.onSurface }, isSelected && { color: theme.colors.primary }]}
                numberOfLines={1}
              >
                {/* If vehicle exists, show registration number as main text, otherwise device label */}
                {position.vehicle?.registration_number || position.device_label}
              </Text>
            </View>
            
            <TouchableOpacity
              onPress={onLocate}
              style={styles.locateButton}
              hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
            >
              <Ionicons name="locate" size={16} color={theme.colors.primary} />
            </TouchableOpacity>
          </View>

          {/* Vehicle/Device Info */}
          {position.vehicle ? (
            <View style={styles.vehicleInfo}>
              {/* Show make and model if available */}
              {(position.vehicle.make || position.vehicle.model) && (
                <Text variant="bodySmall" style={[styles.vehicleMakeModel, { color: theme.colors.onSurfaceVariant }]} numberOfLines={1}>
                  {[position.vehicle.make, position.vehicle.model].filter(Boolean).join(' ')}
                  {position.vehicle.year && ` (${position.vehicle.year})`}
                </Text>
              )}
              {/* Show device label as secondary info */}
              <View style={styles.deviceInfoRow}>
                <Text variant="bodySmall" style={[styles.deviceLabel, { color: theme.colors.onSurfaceVariant }]} numberOfLines={1}>
                  {position.device_label}
                </Text>
                {onEditVehicleLink && (
                  <TouchableOpacity
                    onPress={onEditVehicleLink}
                    style={styles.editButton}
                    hitSlop={{ top: 4, right: 4, bottom: 4, left: 4 }}
                  >
                    <Ionicons name="create-outline" size={12} color={theme.colors.onSurfaceVariant} />
                  </TouchableOpacity>
                )}
              </View>
            </View>
          ) : (
            <View style={styles.noVehicleInfo}>
              <Text variant="bodySmall" style={[styles.deviceLabel, { color: theme.colors.onSurfaceVariant }]} numberOfLines={1}>
                {position.device_label}
              </Text>
              <TouchableOpacity
                onPress={onEditVehicleLink}
                style={styles.linkVehicleButton}
              >
                <Ionicons name="add-circle-outline" size={12} color={theme.colors.primary} />
                <Text variant="bodySmall" style={[styles.linkVehicleText, { color: theme.colors.primary }]}>{t('gps.linkVehicle')}</Text>
              </TouchableOpacity>
            </View>
          )}

          {/* Metrics */}
          <View style={styles.metrics}>
            <View style={styles.metric}>
              <Ionicons name="speedometer-outline" size={12} color={theme.colors.onSurfaceVariant} />
              <Text variant="bodySmall" style={[styles.metricValue, { color: theme.colors.onSurfaceVariant }]}>{speedKmh} km/h</Text>
            </View>
            
            <View style={styles.metric}>
              <Ionicons name="time-outline" size={12} color={theme.colors.onSurfaceVariant} />
              <Text variant="bodySmall" style={[styles.metricValue, { color: theme.colors.onSurfaceVariant }]}>{formatTimestamp(position.timestamp)}</Text>
            </View>
          </View>

          {/* Status Badge */}
          <View style={styles.footer}>
            <View style={[styles.statusBadge, { backgroundColor: theme.colors.surfaceVariant }, position.is_online && { backgroundColor: theme.colors.primaryContainer }]}>
              <View style={[styles.statusDot, { backgroundColor: statusColor }]} />
              <Text variant="labelSmall" style={[styles.statusText, { color: theme.colors.onSurfaceVariant }, position.is_online && { color: theme.colors.primary, fontWeight: '700' }]}>
                {position.is_online ? t('gps.online') : t('gps.offline')}
              </Text>
            </View>
          </View>
        </View>
      </Surface>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 8,
    marginBottom: 8,
    overflow: 'hidden',
    borderWidth: 1,
    flexDirection: 'row',
  },
  cardSelected: {
  },
  statusBar: {
    width: 3,
  },
  cardContent: {
    flex: 1,
    padding: 12,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flex: 1,
  },
  deviceName: {
    fontWeight: '600',
    flex: 1,
  },
  deviceNameSelected: {
  },
  locateButton: {
    padding: 4,
  },
  vehicleInfo: {
    marginBottom: 8,
  },
  vehicleMakeModel: {
    marginBottom: 2,
  },
  deviceInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  deviceLabel: {
    flex: 1,
  },
  noVehicleInfo: {
    marginBottom: 8,
  },
  editButton: {
    padding: 2,
  },
  linkVehicleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginBottom: 8,
  },
  linkVehicleText: {
    fontWeight: '500',
  },
  metrics: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 8,
  },
  metric: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metricValue: {
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  statusBadgeOnline: {
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.3,
    shadowRadius: 2,
    elevation: 2,
  },
  statusText: {
    fontWeight: '600',
  },
  statusTextOnline: {
  },
});
